import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useBankContext, useBrandContext } from '../../../dictionaries/context/BankContext';
import { Profile, TwilioPhoneVerificationType, useNewTwilioPhoneVerificationMutation, useTwilioPhoneVerificationMutation, useUnvalidateTwilioVerificationMutation } from '../../../generated/graphql';
import eventsTracking from '../../../utils/eventsTracking';
import { getFormikValidator } from '../../../utils/getFormikValidator';
import useTimer from '../../../utils/useTimer';
import { useIdentityConfirmation } from './useIdentityConfirmation';

const initialValues = {
  code: '',
  policiesAndTermsA: false,
  policiesAndTermsB: false,
};

interface UseIdentificationConfirmationPhoneCodeProps {
  parentUrl: string;
  profile?: Omit<Profile, 'financialSituation'>;
  refetchConfirmationOptions?: () => Promise<any>;
}

export const useIdentificationConfirmationPhoneCode = (
  { parentUrl, profile, refetchConfirmationOptions }: UseIdentificationConfirmationPhoneCodeProps,
) => {
  const history = useHistory();
  const { digits, ...params } = useParams<{ digits?: string, phone?: string }>();
  const [ brand ] = useBrandContext();
  const [ bank ] = useBankContext();
  const { reset, remaining, isActive } = useTimer({
    initialActive: false,
    stopAt: 15,
  });
  const [ type, setType ] = useState<TwilioPhoneVerificationType | null>(null);

  const [ verifyCode,
    {
      data: phoneVerificationData,
      loading: phoneVerificationLoading,
      error,
      finished,
    },
  ] = useIdentityConfirmation(
    useTwilioPhoneVerificationMutation,
    'twilioPhoneVerification',
    parentUrl,
    {

      errorPolicy: 'ignore',
      onError: async () => {
        if (refetchConfirmationOptions) await refetchConfirmationOptions();
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        formik.setErrors({ code: 'Código inválido' });
      },
    },
  );

  const isRevalidating = !!profile?.phone;
  const phone = params.phone || profile?.phone;
  const isValid = !!phone || !!digits;

  const [
    newVerification,
    { loading: newVerLoading, error: newVerError },
  ] = useNewTwilioPhoneVerificationMutation();
  const [
    unvalidateTwilioVerification,
    { loading: unvalidateLoading },
  ] = useUnvalidateTwilioVerificationMutation();

  const handleBack = async () => {
    if (isRevalidating) {
      await unvalidateTwilioVerification();
      history.push(parentUrl);
      return;
    }
    history.goBack();
  };

  const rulesFilter = (data: any, rules: any) => {
    if (isRevalidating) {
      // eslint-disable-next-line no-param-reassign
      delete rules.policiesAndTermsA;
      // eslint-disable-next-line no-param-reassign
      delete rules.policiesAndTermsB;
    }
  };

  const formik = useFormik({
    initialValues,
    validate: getFormikValidator({
      code: 'required|digits:6',
      policiesAndTermsA: 'accepted',
      policiesAndTermsB: 'accepted',
    }, undefined, rulesFilter as any, {
      required: 'Este campo es obligatorio',
      accepted: 'Debes aceptar este campo',
      digits: 'El código es de 6 dígitos',
    }),
    onSubmit: async ({
      code,
      policiesAndTermsA: termsAndConditions,
      policiesAndTermsB: dataProcessing,
    }) => {
      await verifyCode({
        variables: {
          code,
          brand: brand!,
          bank: bank!,
          termsAndConditions,
          dataProcessing,
        } });
    },
  });

  const sendCode = useCallback((twilioType: TwilioPhoneVerificationType) => {
    if (!isValid) return;
    reset(true);
    newVerification({ variables: { phone, digits, type: twilioType } });
    setType(twilioType);
  }, [ newVerification, phone, digits, reset, isValid ]);

  useEffect(() => {
    if (finished && !error && profile?.user.id) {
      eventsTracking.account.verifyPhone(
        profile.user.id, phoneVerificationData?.twilioPhoneVerification.user.phone,
      );
    }
  }, [
    finished,
    error,
    profile?.user.id,
    phoneVerificationData?.twilioPhoneVerification.user.phone,
  ]);

  const loading = phoneVerificationLoading
    || formik.isSubmitting
    || newVerLoading
    || unvalidateLoading;

  return {
    isValid,
    digits,
    phone,
    formik,
    type,
    newVerError,
    isActive,
    remaining,
    sendCode,
    handleBack,
    loading,
  };
};
